import { useDispatch, useSelector } from "react-redux";
import ProductService from "../services/ProductService";
import useAlerts from "./useAlerts";
import {
    loadingProducts,
    toggleProductState,
    setPageSize,
    updateProduct,
    setProductDetail,
    initialStateForm,
    loadingProductsDropdown,
    addProduct,
    updateProductField,
    resetProductForm,
    setIsCreateProductModalOpen,
    setIsEditProductModalOpen,
    setIsViewProductModalOpen,
    resetFormOnItemTypeChange,
    setIsSubmitting,
    setProductErrors,
    clearProductErrors,
} from "../store/slices/product/productSlice";
import type { RootState, AppDispatch } from "../store/store";
import { AxiosResponse } from "axios";
import useLoading from "./useLoading";
import { clearReservedCode } from "../store/slices/product/productCodeSlice";
import { ProductFormValues } from "../types/product";
import useModals from "./useModals";
import useApiErrorHandler from "./useApiErrorHandler";

export const useProduct = () => {
    const { show: showAlert } = useAlerts();
    const dispatch: AppDispatch = useDispatch();
    const { openModal } = useModals();
    const { handle } = useApiErrorHandler();
    const { showLoadingIndicator, hideLoadingIndicator } = useLoading();

    const {
        form,
        products,
        productsDropdown,
        paginator,
        pageSize,
        isCreateProductModalOpen,
        isEditProductModalOpen,
        isViewProductModalOpen,
        isSubmitting,
        formErrors
    } = useSelector(
        (state: RootState) => state.products
    );

    const handlerSearchProducts = async ({ searchType, query = "", page = 0, size = pageSize }) => {
        try {
            showLoadingIndicator();

            let result: AxiosResponse<any, any>;
            const trimmedQuery = query.trim();

            switch (searchType) {
                case "all":
                    // Obtener todos los productos con paginación
                    result = await ProductService.getAllProductsPaged(page, size);
                    break;

                case "query":
                    if (!trimmedQuery) {
                        showAlert("warning", "Entrada inválida", "Debe ingresar un término de búsqueda válido.");
                        hideLoadingIndicator();
                        return;
                    }
                    result = await ProductService.searchProductsPaged(trimmedQuery, page, size);
                    break;

                default:
                    showAlert("error", "Error", `Tipo de búsqueda no válido: ${searchType}`);
                    hideLoadingIndicator();
                    return;
            }

            // console.log(result)

            dispatch(loadingProducts(result.data));

        } catch (error) {
            handle(error);
        } finally {
            hideLoadingIndicator();
        }
    };


    /**
     * Maneja la activación/desactivación de un producto.
     */
    const handlerToggleProductState = async (id: number) => {
        try {
            const response = await ProductService.toggleProductStatus(id);
            if (response.data?.statusCode === 200) {
                dispatch(toggleProductState(response.data?.data));
                showAlert(response.data?.type, response.data?.title, response.data?.message);
            } else {
                showAlert("error", "Error", "No se pudo cambiar el estado del producto.");
            }
        } catch (error) {
            handle(error);
        }
    };

    const handlerAddOrUpdateProduct = async (data: ProductFormValues, file?: File) => {
        // console.log(data);
        try {
            showLoadingIndicator();
            startSubmitting();

            if (!data.id || data.id === 0) {
                const { id, ...productData } = data;
                const response = await ProductService.createProduct(productData, file);

                console.log(response);

                // console.log(response);
                const { title, message, type, data: newProduct, statusCode } = response;
                if (statusCode === 201) {
                    dispatch(addProduct(newProduct));
                    dispatch(clearReservedCode());
                    openModal(type, { title, message, });
                    dispatch(setIsCreateProductModalOpen(false));
                    dispatch(resetProductForm())
                    resetErrors();
                }

            } else {
                const response = await ProductService.updateProduct(data.id, data, file);
                // console.log(response);
                const { title, message, type, data: updatedProduct, statusCode } = response;
                if (statusCode === 200) {
                    dispatch(updateProduct(updatedProduct));
                    openModal(type, { title, message, });
                    dispatch(setIsEditProductModalOpen(false));
                    dispatch(resetProductForm())
                    resetErrors();
                }
            }
        } catch (error) {
            // console.log(error);
            // Protección contra errores sin "data"
            const backend = error?.response?.data;
            const validationErrors = backend?.data ?? {};

            console.log(validationErrors);

            dispatch(setProductErrors(validationErrors));

            handle(error);
        } finally {
            hideLoadingIndicator();
            stopSubmitting();
        }
    };



    /**
     * ✅ Obtiene un producto por ID y actualiza el estado global.
     */
    const handlerGetProductById = async (id: number) => {
        try {
            showLoadingIndicator();
            const response = await ProductService.getProductById(id);

            const {
                data,
                statusCode
            } = response;

            if (statusCode === 200) {
                dispatch(setProductDetail(data));
            }
            else {
                showAlert("error", "Error", "No se pudo obtener la información del producto.");
            }
        } catch (error) {
            handle(error);
        } finally {
            hideLoadingIndicator();
        }
    };


    /**
 * ✅ Obtiene productos filtrados por tipo de ítem para dropdowns.
 * Se filtra por el código del ItemType (1=bienes, 2=servicios, 3=ambos, 4=otros).
 */
    const handlerGetProductsForDropdownByItemType = async (itemTypeCode: number) => {
        try {
            const response = await ProductService.getProductsForDropdownByItemType(itemTypeCode);
            dispatch(loadingProductsDropdown(response.data));
        } catch (error) {
            handle(error);
        }
    };


    /**
     * ✅ Obtiene productos en formato dropdown y actualiza el estado global.
     */
    const handlerGetProductsForDropdown = async () => {
        try {
            const response = await ProductService.getForDropdown();
            dispatch(loadingProductsDropdown(response.data));
        } catch (error) {
            handle(error);
        }
    };


    /**
     * Cambia el tamaño de la paginación.
     */
    const updatePageSize = (size: number) => {
        dispatch(setPageSize(size));
    };

    const startSubmitting = () => {
        dispatch(setIsSubmitting(true));
    };

    const stopSubmitting = () => {
        dispatch(setIsSubmitting(false));
    };

    // ✅ Limpia todos los errores del formulario
    const resetErrors = () => {
        dispatch(clearProductErrors());
    };

    /**
     * ✅ Obtiene la imagen de un producto a partir de su nombre de archivo.
     */
    const handlerGetProductImage = async (filename: string) => {
        try {
            const imageUrl = await ProductService.getProductImage(filename);
            return imageUrl; // Devuelve la URL de la imagen generada
        } catch (error) {
            console.error("Error obteniendo la imagen del producto:", error);
            return null;
        }
    };


    /**
 * Obtiene costo y stock actual del producto.
 */
    const handlerGetInventoryInfo = async (
        productId: number
    ) => {

        try {

            const response =
                await ProductService
                    .getInventoryInfo(productId);

            const {
                statusCode,
                data
            } = response;

            if (statusCode === 200) {
                return data;
            }

            return null;

        } catch (error) {

            handle(error);

            return null;
        }

    };


    return {
        // Estados
        form,
        formErrors,
        isSubmitting,
        products,
        productsDropdown,
        paginator,
        pageSize,
        initialStateForm,
        isCreateProductModalOpen,
        isEditProductModalOpen,
        isViewProductModalOpen,

        // Funciones
        handlerSearchProducts,
        handlerToggleProductState,
        handlerAddOrUpdateProduct,

        handlerGetProductsForDropdownByItemType,
        handlerGetProductsForDropdown,
        handlerGetProductById,
        updatePageSize,
        handlerGetProductImage,
        handlerGetInventoryInfo,


        updateField: <K extends keyof ProductFormValues>(
            field: K,
            value: ProductFormValues[K]
        ) => dispatch(updateProductField({ field, value })),

        clearForm: () => dispatch(resetProductForm()),

        // 🔓 Abrir y cerrar modal de registro
        openCreateProductModal: () => dispatch(setIsCreateProductModalOpen(true)),
        closeCreateProductModal: () => {
            dispatch(resetProductForm()); // ✅ Limpiar form
            dispatch(setIsCreateProductModalOpen(false));
            dispatch(clearProductErrors());
        },

        // ✏️ Abrir y cerrar modal de edición
        openEditProductModal: () => dispatch(setIsEditProductModalOpen(true)),
        closeEditProductModal: () => {
            dispatch(resetProductForm()); // ✅ Limpiar form
            dispatch(setIsEditProductModalOpen(false));
            dispatch(clearProductErrors());
        },

        // 👁️ Abrir y cerrar modal de visualización
        openViewProductModal: () => dispatch(setIsViewProductModalOpen(true)),
        closeViewProductModal: () => {
            dispatch(resetProductForm()); // ✅ Limpiar form
            dispatch(setIsViewProductModalOpen(false));
        },

        resetFormOnItemTypeChange: (itemTypeId: number, itemTypeCode: number) => {
            dispatch(
                resetFormOnItemTypeChange({
                    itemTypeId,
                    itemTypeCode,
                    code: form.code,
                })
            );
        },

        resetErrors,

        startSubmitting,
        stopSubmitting,


    };
};
